import { Loadable } from '@/components/loaders';
import { setWorkspaceUsersPagination } from '@/features/settings/settingsSlice';
import { isCompanyAdmin, isWorkspaceAdmin } from '@/features/userInfo/hooks/useUserInfo';
import DeleteWorkspaceUserModal from '@/features/workspaceEdit/components/DeleteWorkspaceUserModal';
import { useModalContext } from '@/hooks/useModalContext';
import { useSlideOverContext } from '@/hooks/useSlideOverContext';
import { FrontTable, TablePager } from '@/lib';
import { CompanyId } from '@/models/company';
import { WorkspaceId, WorkspaceUserWithRole } from '@/models/workspace';
import { RootState } from '@/reducers';
import { useGetWorkspaceUsersCountQuery, useLazyWorkspaceUsersQuery } from '@/services/endpoints/workspaces';
import { useAuth0 } from '@auth0/auth0-react';
import { ColumnDef, Row } from '@tanstack/react-table';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { UserDetailSlideOver } from '../UserDetailSlideOver';
import { getWorkspaceUsersTableColumns } from './columns';
import { TableRowType } from './types';

type WorkspaceUsersTableProps = PropsWithChildren<{
  workspaceId: WorkspaceId;
  companyId: CompanyId;
}>;

export const WorkspaceUsersTable: FC<WorkspaceUsersTableProps> = ({ workspaceId, companyId, children }) => {
  const { user } = useAuth0();
  const { openModal } = useModalContext();
  const { openSlideOver } = useSlideOverContext();

  const { t } = useTranslation('ALL');

  const [getWorkspaceUsers, { data, isFetching: isUsersFetching, isLoading: isUsersLoading }] = useLazyWorkspaceUsersQuery();
  const [users, setUsers] = useState<WorkspaceUserWithRole[]>([]);
  const usersWithId = useMemo<TableRowType[]>(() => users.map((u) => ({ ...u, id: u.user.id })), [users]);

  const searchText = useSelector((state: RootState) => state.settings.ui.workspaceUsers?.searchText || '');
  const validSearchText = searchText && searchText.length > 2 ? searchText : undefined;

  const { data: totalCount, isFetching: isTotalCountFetching } = useGetWorkspaceUsersCountQuery({
    workspaceId,
    searchText: validSearchText,
  });

  const isAdmin = isCompanyAdmin(companyId) || isWorkspaceAdmin(workspaceId);

  const queryCallback = useCallback(
    (pageNo: number, pageSize: number) => {
      getWorkspaceUsers({
        workspaceId,
        offset: pageNo * pageSize,
        limit: pageSize,
        searchText: validSearchText,
      });
    },
    [workspaceId, validSearchText]
  );

  const handleRowClick = (row: Row<TableRowType>) => {
    const { email, firstName, lastName, id } = row.original.user;
    openSlideOver({
      renderContent: UserDetailSlideOver,
      renderContentProps: {
        showReset: false,
        showSave: user?.sub === id,
        user: { email, firstName, lastName, id, role: row.original.role.join(', ') },
      },
      showButtonInsideSlideOver: true,
    });
  };

  const handleDeleteUserClick = useCallback(
    (userToDelete: WorkspaceUserWithRole) => {
      openModal({
        renderContent: DeleteWorkspaceUserModal,
        renderContentProps: {
          workspaceId,
          user: userToDelete.user,
        },
        dismissable: true,
        fullWidth: false,
        fullHeight: false,
      });
    },
    [workspaceId, openModal]
  );

  const columns = useMemo(() => {
    const canDelete = isAdmin && Boolean(user?.sub);
    return getWorkspaceUsersTableColumns(
      canDelete,
      canDelete
        ? (userToDelete) => {
            if (user?.sub !== userToDelete.user.id) {
              handleDeleteUserClick(userToDelete);
            }
          }
        : undefined
    );
  }, [isAdmin, user?.sub, handleDeleteUserClick]);

  // TODO: Test with more than 2 page users
  return (
    <TablePager
      stateSelector={(state) => state.settings.ui.workspaceUsers?.pagination}
      stateUpdater={setWorkspaceUsersPagination}
      query={queryCallback}
      queryResponse={data ? data.items : undefined}
      setItems={setUsers}
      querying={isTotalCountFetching || isUsersFetching}
      totalCount={totalCount || 0}
      wrapperClassNames='mb-0'
    >
      {(pager) => (
        <div className='space-y-4'>
          <Loadable isLoading={isUsersLoading}>
            <FrontTable<TableRowType>
              tableData={usersWithId}
              columns={columns as ColumnDef<TableRowType>[]}
              onRowClick={handleRowClick}
              notFound={t('noUserFound')}
            />
          </Loadable>
          {children}
          {pager}
        </div>
      )}
    </TablePager>
  );
};
